/**
 * In-product Settings / About guide copy for keys & recovery.
 * Not full docs - scannable portal prose only.
 */

import { settingsHash } from "./settings-sections.js";

/** @type {Record<string, { title: string, body: string }>} */
export const SETTINGS_EXPLAINERS = {
  account: {
    title: "Account",
    body: "Soft onboarding binds your Moonlight wallet to a participant id, then to a trading key, so you can deposit and trade. Setup tools live here.",
  },
  security: {
    title: "Security",
    body: "Two secrets: Moonlight (gas, funding, NFT mint) and the trading key (signs trades). Read this before exporting a backup. Data handling is in the privacy policy.",
  },
  creator: {
    title: "Creator",
    body: "After a streak achievement NFT, post a participation bond from your Moonlight balance to unlock Create. Owner and create-admin wallets skip the bond.",
  },
  preferences: {
    title: "Preferences",
    body: "Local-only options for this browser. They do not sync across devices. The privacy policy lists these keys.",
  },
};

/**
 * @typedef {{ id: string, title: string, body: string, planned?: boolean }} SecurityGuideSection
 */

/** @type {SecurityGuideSection[]} */
export const SECURITY_GUIDE = [
  {
    id: "two-keys",
    title: "Two keys",
    body: "Your Moonlight wallet pays gas, funds deposits, receives achievement NFTs, and (when revoke ships) is the recovery identity for free collateral. Your trading key signs deposits, trades, and claims - positions and streaks are keyed by its commitment, not your Moonlight address.",
  },
  {
    id: "what-we-store",
    title: "What we store in this browser",
    body: "The portal may cache your trading key in this browser so a refresh does not force another signature. Disconnect and Clear key remove the local participant record and trading key. The privacy policy lists every localStorage key and what the API stores.",
  },
  {
    id: "backups",
    title: "Backups",
    body: "Export backup downloads JSON that includes your trading secret key - treat it like a seed phrase. Import restores that trading identity. If a different commitment is already linked to your participant, Import is refused so you do not orphan positions. Password-wrapped export is coming.",
    planned: false,
  },
  {
    id: "if-leaks",
    title: "If the trading key leaks",
    body: "Anyone who has the secret can sign as that commitment until collateral is gone. Disconnect only clears this browser - it is not an on-chain revoke.",
  },
  {
    id: "recovery",
    title: "Revoke and recovery (coming)",
    body: "Planned: on-chain revoke stops further trades for that commitment even if the secret still exists. Free collateral would then move only via the Moonlight wallet linked in soft onboarding. Prefer withdrawing while you still hold the key if you are not under active theft.",
    planned: true,
  },
  {
    id: "live-vs-coming",
    title: "Live vs coming",
    body: "Live today: soft onboard, trading-key cache in this browser, Import/Export, Disconnect clears local identity. Coming: on-chain revoke and linked-Moonlight recovery, password on Export, optional passphrase unlock for the browser cache.",
    planned: true,
  },
];

export const ABOUT_SECURITY_CTA = {
  label: "Keys, backups & recovery",
  hash: settingsHash("security"),
};
